import { useState } from 'react';
import { useAddFavourite, useFavourites, useRemoveFavourite } from './use-favourites';

/**
 * "Save for next time" on someone the employer has worked with.
 * [Job Poster step 30]
 *
 * Hidden entirely when the api would refuse it, rather than shown and then
 * erroring — a control that only fails is worse than no control.
 */
export function FavouriteButton({ teenId, teenName }: { teenId: string; teenName: string }) {
  const [error, setError] = useState<string | null>(null);

  const favourites = useFavourites({ pageSize: 100 });
  const add = useAddFavourite();
  const remove = useRemoveFavourite();

  const saved = (favourites.data?.items ?? []).some((entry) => entry.teen.id === teenId);

  async function toggle(): Promise<void> {
    setError(null);
    try {
      if (saved) await remove.mutateAsync(teenId);
      else await add.mutateAsync({ teenId });
    } catch {
      // The only realistic refusal is "you have not worked together yet",
      // which is a fact about the relationship rather than a failure.
      setError('You can save someone once they have accepted one of your jobs.');
    }
  }

  const busy = add.isPending || remove.isPending;

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={busy}
        aria-pressed={saved}
        aria-label={saved ? `Remove ${teenName} from saved` : `Save ${teenName} for future jobs`}
        className={`text-xs font-semibold transition disabled:opacity-50 ${
          saved ? 'text-sun-600 hover:text-sun-700' : 'text-slate-400 hover:text-sun-600'
        }`}
      >
        {saved ? '★ Saved' : '☆ Save for next time'}
      </button>
      {error && <span className="text-xs font-medium text-red-600">{error}</span>}
    </span>
  );
}
